import type * as Sentry from '@sentry/nuxt'
import { defineIntegration } from '@sentry/core'
import { escapeRegExp, omitBy, isString } from 'lodash-es'
import type { H3Error } from 'h3'
import type { FetchError } from 'ofetch'
import type { Feature } from '#app'

/**
 * Most of the client errors are not important to track in Sentry
 * It could be helpful only in rare cases, so it is better to ignore most of them
 */
export const SENTRY_ERROR_CODES_TO_IGNORE = new Set([
  401, // Unauthorized
  403, // Forbidden
  404, // Not found
  422, // Unprocessable Content
])

/**
 * Sentry callback to skip sending certain types of errors to Sentry
 * @param event
 * @param hint
 * @returns
 */
export function beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
  const error = hint && hint.originalException
  if (error && (error as any).response) {
    const { response } = error as any
    const statusCode = response.status
    if (SENTRY_ERROR_CODES_TO_IGNORE.has(statusCode)) {
      // Discard event
      return null
    }
  }
  return event
}

const FEATURE_PREFIX = 'feature.' as const

export const featureFlagsIntegration = defineIntegration(() => {
  return {
    name: 'FeatureFlags',

    processEvent(event, _hint, _client) {
      return moveFeatureTagsToFeatureFlags(event)
    },
  }
})

export function mapFeatureToSentryTagEntry(feature: Feature) {
  const tagName = `${FEATURE_PREFIX}${feature.name.toLowerCase().replace(/[^a-z0-9]+/gi, '.')}`
  const tagValue = getFeatureVariantEnabled(feature)?.name ?? feature.isEnabled

  return [tagName, tagValue] as const
}

export function moveFeatureTagsToFeatureFlags(event: Sentry.Event) {
  if (!event.contexts) {
    event.contexts = {}
  }

  if (!event.contexts.flags) {
    event.contexts.flags = { values: [] }
  }

  if (!event.tags) return event

  const { tags } = event

  event.tags = omitBy(event.tags, (val, key) => isFeatureTagEntry([key, val]))

  event.contexts.flags.values = [
    ...event.contexts.flags.values,
    ...Object.entries(tags)
      .filter(isFeatureTagEntry)
      .map(mapFeatureTagEntryToFlag),
  ]
  return event
}

function mapFeatureTagEntryToFlag([name, value]: [string, string | boolean]) {
  const flagName = name
    .replace(new RegExp(`^${escapeRegExp(FEATURE_PREFIX)}`), '')
    .replace(/[^a-z0-9]+/gi, '_')
    .toUpperCase()
  const flagSuffix = isString(value) ? `_${value.toUpperCase()}` : ''

  return {
    flag: flagName + flagSuffix,
    result: Boolean(value),
  }
}

function isFeatureTagEntry(
  entry: [string, unknown],
): entry is [`${typeof FEATURE_PREFIX}.${string}`, string | boolean] {
  const [key, value] = entry

  return (
    typeof key === 'string' &&
    key.startsWith(FEATURE_PREFIX) &&
    ['string', 'boolean'].includes(typeof value)
  )
}

export function isError500(error: H3Error | FetchError): boolean {
  return !!error.statusCode && error.statusCode >= 500 && error.statusCode < 600
}
